import Headline from '@allesauto/core/headline';
import NextImage from '@allesauto/core/nextImage';
import { FieldTypeAsset } from '@allesauto/utils/*';
import { sbEditable } from '@storyblok/storyblok-editable';
import clsx from 'clsx';
import { GiCheckMark } from 'react-icons/gi';

enum Colors {
  BLACK = 'BLACK',
  GREY = 'GREY',
  PURPLE = 'PURPLE',
  WHITE = 'WHITE',
  GREEN = 'GREEN',
}

enum Variant {
  HEADLINE_FIRST = 'HEADLINE',
  IMAGE_FIRST = 'IMAGE',
}

interface CheckListBox {
  headline: string;
  headlineTag: 3 | 4;
  headlineColor: keyof Omit<typeof Colors, Colors.GREY>;
  bulletPoints: {
    _uid: string;
    text: string;
    color: keyof Omit<typeof Colors, Colors.GREY>;
  }[];
  bulletPointsColor: Colors.BLACK | Colors.WHITE;
  background: keyof Omit<typeof Colors, Colors.BLACK>;
}

interface TextChecklistPictureProps {
  _uid: string;
  component: string;
  headline: string;
  headlineTag: 2 | 3 | 4;
  headlineFontSize: 'title' | 'headline2' | 'headline3' | 'headline4';
  headlineColor: Colors.GREY | Colors.PURPLE | Colors.GREEN;
  text?: string;
  background: Colors.GREY | Colors.PURPLE | Colors.GREEN;
  image: FieldTypeAsset;
  checklistBox: [CheckListBox];
  variant: Variant;
}

const cssClasses = {
  container:
    'px-15px py-30px md:py-60px md:px-30px md lg:px-60px grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
  checklistContainer: 'p-30px md:p-40px lg:p-[48px]',
  headline: 'mb-20px',
  headlineContainer: 'lg:pr-[60px] md:col-span-3 mb-30px lg:mb-0 lg:col-span-1',
  imageFirstImageContainer: 'md:col-span-3 mb-30px lg:mb-0 lg:col-span-1',
  imageFirstHeadline: 'sm:pb-[16px] md:pr-40px lg:pl-40px',
  checklistItem: 'flex gap-[16px] items-center mt-[24px]',
  checklistIcon: 'text-[16px]',
};

const themes = {
  [Colors.BLACK]: {
    background: 'bg-black monochrome:bg-black',
    text: 'text-black monochrome:text-black',
  },
  [Colors.PURPLE]: {
    background: 'bg-purple80 monochrome:bg-purple80',
    text: 'text-purple80 monochrome:text-purple80',
  },
  [Colors.WHITE]: {
    background: 'bg-white monochrome:bg-white',
    text: 'text-white monochrome:text-white',
  },
  [Colors.GREEN]: {
    background: 'bg-green60 monochrome:bg-green60',
    text: 'text-green60 monochrome:text-green60',
  },
  [Colors.GREY]: {
    background: 'bg-grey10 monochrome:bg-grey10',
    text: 'text-grey20 monochrome:text-grey20',
  },
};

const TextChecklistPicture = (props: TextChecklistPictureProps) => {
  const {
    headline,
    headlineTag,
    headlineFontSize,
    headlineColor,
    text,
    background,
    image,
    variant = Variant.HEADLINE_FIRST,
    checklistBox: [checklistBox],
  } = props;

  if (variant === Variant.IMAGE_FIRST) {
    return (
      <section
        className={clsx(cssClasses.container, themes[background].background)}
        {...sbEditable(props)}
      >
        <NextImage
          alt={image.alt}
          src={image.filename}
          layout="fill"
          objectFit="cover"
          objectPosition="center"
          className={cssClasses.imageFirstImageContainer}
          imgClassName="!static"
        />

        <div className={cssClasses.imageFirstHeadline}>
          <Headline
            headlineTag={headlineTag}
            headlineText={headline}
            headlineColor={themes[headlineColor].text}
            headlineClassName={clsx(
              cssClasses.headline,
              headlineFontSize && `text-${headlineFontSize}`
            )}
          />
          {text && <p>{text}</p>}
        </div>

        <div
          className={clsx(
            cssClasses.checklistContainer,
            themes[checklistBox.bulletPointsColor].text,
            themes[checklistBox.background].background
          )}
        >
          <Headline
            headlineTag={checklistBox.headlineTag}
            headlineText={checklistBox.headline}
            headlineColor={themes[checklistBox.headlineColor].text}
          />
          {checklistBox.bulletPoints.map((bulletPoint) => (
            <div className={cssClasses.checklistItem} key={bulletPoint._uid}>
              <GiCheckMark
                className={clsx(
                  cssClasses.checklistIcon,
                  themes[checklistBox.headlineColor].text
                )}
              />
              <p>{bulletPoint.text}</p>
            </div>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section
      className={clsx(cssClasses.container, themes[background].background)}
      {...sbEditable(props)}
    >
      <div className={cssClasses.headlineContainer}>
        <Headline
          headlineTag={headlineTag}
          headlineText={headline}
          headlineColor={themes[headlineColor].text}
          headlineClassName={clsx(
            cssClasses.headline,
            headlineFontSize && `text-${headlineFontSize}`
          )}
        />
        {text && <p>{text}</p>}
      </div>

      <div
        className={clsx(
          cssClasses.checklistContainer,
          themes[checklistBox.bulletPointsColor].text,
          themes[checklistBox.background].background
        )}
      >
        <Headline
          headlineTag={checklistBox.headlineTag}
          headlineText={checklistBox.headline}
          headlineColor={themes[checklistBox.headlineColor].text}
        />
        {checklistBox.bulletPoints.map((bulletPoint) => (
          <div className={cssClasses.checklistItem} key={bulletPoint._uid}>
            <GiCheckMark
              className={clsx(
                cssClasses.checklistIcon,
                themes[checklistBox.headlineColor].text
              )}
            />
            <p>{bulletPoint.text}</p>
          </div>
        ))}
      </div>

      <NextImage
        alt={image.alt}
        src={image.filename}
        layout="fill"
        objectFit="cover"
        objectPosition="center"
        imgClassName="!static"
        className="h-full w-full"
      />
    </section>
  );
};

export default TextChecklistPicture;
